import { AnimatePresence, motion, useScroll } from 'framer-motion'
import { ArrowUpRight, ChevronDown, Menu, MessageCircle, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { company, localePath, services, ui, type Locale } from '../content'
import { choixGarde, garderChoix, rouvrirBandeau } from '../consentement'
import { switchLocalePath } from '../lib'
import { languesDe } from '../site-pages'
import { villes } from '../villes'

export function Layout({ locale, children }: { locale: Locale; children: React.ReactNode }) {
  return <>
    <ScrollProgress />
    <Header locale={locale} />
    <main>{children}</main>
    <Footer locale={locale} />
    <WhatsApp locale={locale} />
    <BandeauCookies locale={locale} />
  </>
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll()
  return <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} />
}

function Header({ locale }: { locale: Locale }) {
  const t = ui[locale]
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  useEffect(() => setOpen(false), [location.pathname])

  // Villes et guides n'existent qu'en français ; en arabe, la FAQ prend leur place.
  const links = locale === 'fr' ? [
    [t.nav.home, localePath(locale)],
    ['Villes', localePath(locale, '/transitaire-maroc')],
    ['Guides', localePath(locale, '/guides')],
    [t.nav.about, localePath(locale, '/a-propos')],
    [t.nav.contact, localePath(locale, '/contact')],
  ] : [
    [t.nav.home, localePath(locale)],
    [t.nav.about, localePath(locale, '/a-propos')],
    ['أسئلة شائعة', localePath(locale, '/faq')],
    [t.nav.contact, localePath(locale, '/contact')],
  ]
  // L'autre langue : la même page si elle existe, sinon son accueil.
  const chemin = location.pathname.replace(/^\/(fr|ar)/, '').replace(/\/+$/, '')
  const autre = locale === 'fr' ? 'ar' : 'fr'
  const autreLien = languesDe(chemin).includes(autre) ? switchLocalePath(location.pathname, autre) : localePath(autre)

  return <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
    <div className="nav-shell">
      <Link className="brand" to={localePath(locale)} aria-label="COGETRANS">
        <img className="logo-clair" src="/assets/cogetrans-logo-clair.png" alt="COGETRANS" width="506" height="167" />
        <img className="logo-sombre" src="/assets/cogetrans-logo-sombre.png" alt="" width="506" height="167" />
      </Link>
      <nav className="desktop-nav" aria-label={locale === 'fr' ? 'Navigation principale' : 'التنقل الرئيسي'}>
        <NavLink to={links[0][1]} end>{links[0][0]}</NavLink>
        <div className="nav-dropdown" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
          <NavLink to={localePath(locale, '/services')} onFocus={() => setServicesOpen(true)}>{t.nav.services}<ChevronDown size={14} /></NavLink>
          <AnimatePresence>{servicesOpen && <motion.div className="mega-menu" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}>
            <div className="mega-menu-intro"><span>COGETRANS</span><strong>{t.home.servicesTitle}</strong></div>
            <div className="mega-menu-links">{services.map((service) => {
              const Icon = service.icon
              return <Link key={service.slug} to={localePath(locale, `/services/${service.slug}`)}><Icon size={19} /><span>{service.title[locale]}</span><ArrowUpRight size={15} /></Link>
            })}{locale === 'fr' && <Link className="mega-menu-all" to={localePath(locale, '/transitaire-maroc')}><span>Nos services partout au Maroc</span><ArrowUpRight size={15} /></Link>}</div>
          </motion.div>}</AnimatePresence>
        </div>
        {links.slice(1).map(([label, href]) => <NavLink key={href} to={href}>{label}</NavLink>)}
      </nav>
      <div className="nav-actions">
        <Link className="lang-switch" to={autreLien} aria-label={locale === 'fr' ? 'العربية' : 'Français'}>{locale === 'fr' ? 'AR' : 'FR'}</Link>
        <Link className="button button-primary button-small" to={localePath(locale, '/demander-un-devis')}>{t.nav.quote}<ArrowUpRight size={16} /></Link>
        <button className="menu-button" onClick={() => setOpen(true)} aria-label={t.common.menu}><Menu /></button>
      </div>
    </div>
    <AnimatePresence>{open && <motion.div className="mobile-menu" initial={{ x: locale === 'ar' ? '-100%' : '100%' }} animate={{ x: 0 }} exit={{ x: locale === 'ar' ? '-100%' : '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 240 }}>
      <div className="mobile-menu-top"><img src="/assets/cogetrans-logo-sombre.png" alt="COGETRANS" width="506" height="167" /><button onClick={() => setOpen(false)} aria-label={t.common.close}><X /></button></div>
      <div className="mobile-links">
        {links.map(([label, href]) => <Link key={href} to={href}>{label}<ArrowUpRight /></Link>)}
        <Link to={localePath(locale, '/services')}>{t.nav.services}<ArrowUpRight /></Link>
        <div className="mobile-services">{services.map((service) => <Link key={service.slug} to={localePath(locale, `/services/${service.slug}`)}>{service.title[locale]}</Link>)}</div>
      </div>
      <Link className="button button-primary" to={localePath(locale, '/demander-un-devis')}>{t.nav.quote}</Link>
    </motion.div>}</AnimatePresence>
  </header>
}

function Footer({ locale }: { locale: Locale }) {
  const t = ui[locale]
  return <footer className="site-footer">
    <div className="footer-top">
      <div className="footer-brand"><div className="footer-logo"><img src="/assets/cogetrans-logo-clair.png" alt="COGETRANS" width="506" height="167" /></div><p>{t.footer.text}</p></div>
      <div><strong>{t.nav.services}</strong>{services.map(s => <Link key={s.slug} to={localePath(locale, `/services/${s.slug}`)}>{s.title[locale]}</Link>)}</div>
      {locale === 'fr' && <div><strong>Villes</strong>{villes.map(v => <Link key={v.slug} to={localePath(locale, `/${v.slug}`)}>Transitaire {v.nom}</Link>)}</div>}
      <div><strong>COGETRANS</strong><Link to={localePath(locale, '/a-propos')}>{t.nav.about}</Link><Link to={localePath(locale, '/comment-ca-fonctionne')}>{t.nav.process}</Link>{locale === 'fr' && <Link to={localePath(locale, '/guides')}>Guides</Link>}<Link to={localePath(locale, '/faq')}>{locale === 'fr' ? 'Questions fréquentes' : 'أسئلة شائعة'}</Link><Link to={localePath(locale, '/demander-un-devis')}>{t.nav.quote}</Link><Link to={localePath(locale, '/contact')}>{t.nav.contact}</Link></div>
      <div><strong>{t.contact.infoTitle}</strong><span>{company.address[locale]}</span>{company.phones.map(t => <a key={t.link} href={`tel:${t.link}`} dir="ltr">{t.display}</a>)}<a href={`mailto:${company.email}`}>{company.email}</a><span>{locale === 'fr' ? `Transitaire agréé en douane · agrément n° ${company.legal.agrement}` : `معشر معتمد لدى الجمارك · اعتماد رقم ${company.legal.agrement}`}</span></div>
    </div>
    <div className="footer-bottom"><span>© {new Date().getFullYear()} COGETRANS. {t.footer.rights} · {t.footer.developedBy} <a className="footer-credit" href="https://www.instagram.com/amine_des/" target="_blank" rel="noopener">Amine</a> – <a className="footer-credit" href="https://pharosystems.com/?utm_source=cogetrans&utm_medium=referral&utm_campaign=credit-site" target="_blank" rel="noopener">Pharo Systems</a></span><div><Link to={localePath(locale, '/mentions-legales')}>{t.footer.legal}</Link><Link to={localePath(locale, '/confidentialite')}>{t.footer.privacy}</Link><Link to={localePath(locale, '/cookies')}>{locale === 'fr' ? 'Cookies' : 'ملفات الارتباط'}</Link><Link to={localePath(locale, '/conditions-utilisation')}>{locale === 'fr' ? 'Conditions d’utilisation' : 'شروط الاستخدام'}</Link><Link to={localePath(locale, '/plan-du-site')}>{locale === 'fr' ? 'Plan du site' : 'خريطة الموقع'}</Link><button type="button" className="footer-cookies" onClick={rouvrirBandeau}>{locale === 'fr' ? 'Gérer les cookies' : 'إدارة ملفات تعريف الارتباط'}</button></div></div>
  </footer>
}

// Bandeau cookies : Refuser et Accepter de même taille ; le bouton WhatsApp flottant est caché tant qu'il est affiché.
function BandeauCookies({ locale }: { locale: Locale }) {
  const [ouvert, setOuvert] = useState(false)
  useEffect(() => {
    if (!choixGarde()) setOuvert(true)
    const ouvrir = () => setOuvert(true)
    window.addEventListener('cogetrans-cookies-ouvrir', ouvrir)
    return () => window.removeEventListener('cogetrans-cookies-ouvrir', ouvrir)
  }, [])
  useEffect(() => { document.body.classList.toggle('bandeau-ouvert', ouvert) }, [ouvert])
  if (!ouvert) return null
  const fr = locale === 'fr'
  const choisir = (c: 'accepte' | 'refuse') => { garderChoix(c); setOuvert(false) }
  return <div className="cookie-banner" role="dialog" aria-label={fr ? 'Cookies' : 'ملفات تعريف الارتباط'}>
    <p>{fr ? 'Nous comptons les visites avec Google Analytics. Sans votre accord, aucun cookie n’est posé.' : 'نحصي الزيارات بواسطة Google Analytics. دون موافقتكم، لا يُوضع أي ملف تعريف ارتباط.'} <Link to={localePath(locale, '/cookies')}>{fr ? 'En savoir plus' : 'اعرف المزيد'}</Link></p>
    <div><button type="button" className="button button-secondary" onClick={() => choisir('refuse')}>{fr ? 'Refuser' : 'رفض'}</button><button type="button" className="button button-dark" onClick={() => choisir('accepte')}>{fr ? 'Accepter' : 'قبول'}</button></div>
  </div>
}

function WhatsApp({ locale }: { locale: Locale }) {
  const href = company.whatsapp ? `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(locale === 'fr' ? 'Bonjour COGETRANS, je souhaite discuter d’une opération import-export.' : 'مرحباً كوجيترانس، أود مناقشة عملية استيراد أو تصدير.')}` : localePath(locale, '/contact')
  return <a className="whatsapp-float" href={href} target={company.whatsapp ? '_blank' : undefined} rel="noreferrer" aria-label={ui[locale].hero.whatsapp}><MessageCircle /><span>{ui[locale].hero.whatsapp}</span></a>
}

export function Reveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  return <motion.div className={className} initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-10%' }} transition={{ duration: .7, delay, ease: [.22, 1, .36, 1] }}>{children}</motion.div>
}

// Plus de surtitre au-dessus des titres (règle R-58 : le titre parle seul). Gardé pour ne pas toucher chaque page.
export function Eyebrow(_: { children: React.ReactNode }) {
  return null
}

export function PageHero({ locale, title, text, image = '/assets/hero-port.webp' }: { locale: Locale; eyebrow?: string; title: string; text: string; image?: string }) {
  return <section className="page-hero" style={{ '--page-image': `url(${image})` } as React.CSSProperties}>
    <div className="page-hero-overlay" />
    <div className="page-hero-content"><motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .8 }}>{title}</motion.h1><motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .25 }}>{text}</motion.p><Link className="button button-primary" to={localePath(locale, '/demander-un-devis')}>{ui[locale].nav.quote}<ArrowUpRight /></Link></div>
  </section>
}
